import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import axios from 'axios'
import { useFormik } from 'formik'
import '../css/FilmBudgetBreakdown.css'
import { STREAM_MESSAGES } from '../utils/filmBudget/constants'
import {
  buildIntelItems,
  buildInvestorStatement,
  calculateBudget,
  parseBudget,
} from '../utils/filmBudget/calculator'
import ProductionToolsHeader from '../components/productionTools/ProductionToolsHeader'
import ProductionToolsSelect from '../components/productionTools/ProductionToolsSelect'
import AiToolSamplePdfButton from '../components/productionTools/AiToolSamplePdfButton'
import AiToolRunButton from '../components/productionTools/AiToolRunButton'
import AiToolPriceBadge from '../components/productionTools/AiToolPriceBadge'
import FieldError from '../components/productionTools/FieldError'
import AiToolPaymentErrorBanner from '../components/productionTools/AiToolPaymentErrorBanner'
import FilmBudgetBreakdownResultsView from '../components/filmBudget/FilmBudgetBreakdownResultsView'
import { useAiToolPayment } from '../hooks/useAiToolPayment'
import { useAiToolPaymentError } from '../hooks/useAiToolPaymentError'
import {
  AI_TOOL_TYPES,
  buildBudgetBreakdownFormData,
} from '../utils/aiTool/payment'
import {
  AI_TOOL_LIMITS,
  budgetBreakdownSchema,
  getFormBannerError,
} from '../utils/aiTool/validationSchemas'
import {
  handleDigitsOnlyChange,
  preventInvalidNumberKeyDown,
  preventNumberInputWheelChange,
} from '../utils/aiTool/numberInputHandlers'
import { getAiToolApiConfig } from '../utils/aiTool/apiConfig'
import { Box } from '@mui/material'

const INITIAL_FORM = {
  projTitle: '',
  email: '',
  uname: '',
  format: '',
  genre: '',
  market: '',
  totalBudget: '',
  shootDays: '',
  runtime: '',
  castCount: '',
  locations: '',
  nightShoots: '',
  vfxShots: '',
  castLevel: 'emerging',
  crewLevel: 'indie',
  locationType: 'practical',
  postLevel: 'basic',
  stunts: 'none',
  period: 'contemporary',
  music: 'minimal',
  distrib: 'festival',
  notes: '',
}

const FilmBudgetBreakdown = () => {
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [budget, setBudget] = useState(null)
  const [inputs, setInputs] = useState(null)
  const [investorStmt, setInvestorStmt] = useState('')
  const [intelItems, setIntelItems] = useState([])
  const [aiContent, setAiContent] = useState('')
  const [aiError, setAiError] = useState('')
  const [contentTypesList, setContentTypesList] = useState([])
  const [genreList, setGenreList] = useState([])
  const [countryList, setCountryList] = useState([])
  const { initiatePayment } = useAiToolPayment()
  const resultsRef = useRef(null)
  const msgTimerRef = useRef(null)
  const [streamMsg, setStreamMsg] = useState(STREAM_MESSAGES[0])

  const showErr = useCallback((msg) => {
    setError(msg)
    setTimeout(() => setError(''), 7000)
  }, [])

  const clearStreamTimer = useCallback(() => {
    if (msgTimerRef.current) {
      clearInterval(msgTimerRef.current)
      msgTimerRef.current = null
    }
  }, [])

  const startStreamTimer = useCallback(() => {
    clearStreamTimer()
    setStreamMsg(STREAM_MESSAGES[0])
    let msgIdx = 0
    msgTimerRef.current = setInterval(() => {
      msgIdx = Math.min(STREAM_MESSAGES.length - 1, msgIdx + 1)
      setStreamMsg(STREAM_MESSAGES[msgIdx])
    }, 2000)
  }, [clearStreamTimer])

  const formik = useFormik({
    initialValues: INITIAL_FORM,
    validationSchema: budgetBreakdownSchema,
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: async (values) => {
      clearPaymentError()
      const parsedInputs = {
        projTitle: values.projTitle.trim() || 'Untitled Project',
        email: values.email.trim(),
        format: values.format,
        genre: values.genre,
        market: values.market,
        totalBudget: parseBudget(values.totalBudget),
        shootDays: parseInt(values.shootDays, 10) || 20,
        runtime: values.runtime.trim(),
        castCount: parseInt(values.castCount, 10) || 3,
        locations: parseInt(values.locations, 10) || 5,
        nightShoots: parseInt(values.nightShoots, 10) || 0,
        vfxShots: parseInt(values.vfxShots, 10) || 0,
        castLevel: values.castLevel,
        crewLevel: values.crewLevel,
        locationType: values.locationType,
        postLevel: values.postLevel,
        stunts: values.stunts,
        period: values.period,
        music: values.music,
        distrib: values.distrib,
        notes: values.notes.trim(),
      }

      setPaymentLoading(true)
      setAnalyzing(false)
      setShowResults(false)
      clearStreamTimer()

      let budgetData
      try {
        budgetData = calculateBudget(parsedInputs)
      } catch (err) {
        clearStreamTimer()
        showErr(`Calculation error: ${err.message}`)
        setPaymentLoading(false)
        return
      }

      try {
        const formData = buildBudgetBreakdownFormData(values, budgetData)

        const paymentResult = await initiatePayment({
          toolType: AI_TOOL_TYPES.BUDGET_BREAKDOWN,
          email: values.email.trim(),
          fullName: values.uname.trim(),
          formData,
          rawFormValues: values,
          onPaymentVerified: () => {
            setPaymentLoading(false)
            setAnalyzing(true)
            startStreamTimer()
          },
          onDismiss: () => {
            setPaymentLoading(false)
          },
        })

        if (paymentResult?.redirecting) {
          return
        }

        const { aiResult } = paymentResult

        const commentary = aiResult?.data?.text?.trim()
        if (!commentary) {
          throw new Error(
            aiResult?.message || 'AI commentary unavailable. Please try again.',
          )
        }

        setBudget(budgetData)
        setInputs({
          ...parsedInputs,
          aiuId: aiResult.data?.aiuId,
          pdfContent: aiResult.data?.pdfContent,
        })
        setInvestorStmt(buildInvestorStatement(budgetData, parsedInputs))
        setIntelItems(buildIntelItems(budgetData, parsedInputs))
        setAiContent(commentary)
        setAiError('')
        setShowResults(true)
      } catch (err) {
        if (err.message === 'Payment cancelled') {
          return
        }
        showErr(
          err?.response?.data?.message ||
            err.message ||
            'Payment failed. Please try again.',
        )
      } finally {
        clearStreamTimer()
        setAnalyzing(false)
        setPaymentLoading(false)
      }
    },
  })

  const { setValues: setFormValues } = formik

  const handleRestoreForm = useCallback(
    (restored) => {
      setFormValues(restored)
    },
    [setFormValues],
  )

  const { paymentError, clearPaymentError } = useAiToolPaymentError(
    AI_TOOL_TYPES.BUDGET_BREAKDOWN,
    handleRestoreForm,
  )

  const displayError = getFormBannerError(formik, error)
  const isSubmitting = paymentLoading || analyzing
  const { setFieldValue, values: formValues } = formik

  const formatOptions = useMemo(
    () => contentTypesList?.map((item) => item.title),
    [contentTypesList],
  )

  const genreOptions = useMemo(
    () => genreList?.map((item) => item.title),
    [genreList],
  )

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/dropdown/list`,
        getAiToolApiConfig(),
      )
      .then((response) => {
        if (response?.data?.data) {
          setContentTypesList(response?.data?.data?.contentType || [])
          setGenreList(response?.data?.data?.genre || [])
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }, [])

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/users/country-list`,
        getAiToolApiConfig(),
      )
      .then((response) => {
        if (response?.data?.data) {
          setCountryList(response?.data?.data)
        }
      })
      .catch((error) => {
        console.error(error)
        setCountryList([])
      })
  }, [])

  useEffect(() => {
    if (genreList.length === 0) return
    if (formValues.genre) return

    const drama = genreList?.find((item) => item.title === 'Drama')
    setFieldValue('genre', drama?.title || genreList[0].title)
  }, [genreList, formValues.genre, setFieldValue])

  useEffect(() => {
    if (countryList.length === 0) return
    if (formValues.market) return

    const india = countryList.find((item) => item.title === 'India')
    setFieldValue('market', india?.title || countryList[0].title)
  }, [countryList, formValues.market, setFieldValue])

  useEffect(() => {
    return () => clearStreamTimer()
  }, [clearStreamTimer])

  useEffect(() => {
    if (showResults && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [showResults, budget])

  const resetForm = () => {
    setShowResults(false)
    setBudget(null)
    setInputs(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="production-tools-theme film-budget-page">
      <Helmet>
        <title>Film Budget Breakdown — PitchBlu</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <ProductionToolsHeader badge="Budget Intelligence" />

      <section className="hero">
        <div className="hero-eyebrow">Industry Standard ATL / BTL Format</div>
        <h1>
          Film Budget
          <br />
          Breakdown
        </h1>
        <p className="hero-sub">
          Enter your project details. Get a professional, investor-ready budget
          breakdown in the exact ATL / BTL format expected by bond companies,
          distributors, and studios worldwide.
        </p>
        <Box>
          <div className="ai-tool-hero-actions">
            <AiToolPriceBadge
              toolType={AI_TOOL_TYPES.BUDGET_BREAKDOWN}
              variant="hero"
            />
            <AiToolSamplePdfButton toolType={AI_TOOL_TYPES.BUDGET_BREAKDOWN} />
          </div>
        </Box>

        {/* <div className="format-pills">
          {formatOptions.map((fmtLabel) => (
            <button
              key={fmtLabel}
              type="button"
              className={`fpill ${formik.values.format === fmtLabel ? 'on' : ''}`}
              onClick={() => formik.setFieldValue('format', fmtLabel)}
            >
              {fmtLabel}
            </button>
          ))}
        </div>
        <FieldError formik={formik} name="format" /> */}
      </section>

      <div className="wrap">
        <div className="card">
          <AiToolPaymentErrorBanner
            message={paymentError}
            onDismiss={clearPaymentError}
          />
          <span className="sec-label">Project Information</span>
          <div className="fgrid">
            <div className="fg">
              <label htmlFor="projTitle">Project Title</label>
              <input
                id="projTitle"
                type="text"
                placeholder="e.g. The Last Rain"
                value={formik.values.projTitle}
                onChange={formik.handleChange}
                name="projTitle"
                maxLength={AI_TOOL_LIMITS.PROJECT_TITLE_MAX}
              />
              <FieldError formik={formik} name="projTitle" />
            </div>
            <div className="fg">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="example@gmail.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                maxLength={AI_TOOL_LIMITS.EMAIL_MAX}
              />
              <FieldError formik={formik} name="email" />
            </div>
            <div className="fg">
              <label htmlFor="uname">Full Name</label>
              <input
                id="uname"
                type="text"
                placeholder="e.g. Ravi Kumar"
                value={formik.values.uname}
                onChange={formik.handleChange}
                name="uname"
                maxLength={AI_TOOL_LIMITS.NAME_MAX}
                autoComplete="name"
              />
              <FieldError formik={formik} name="uname" />
            </div>
            <div className="fg">
              <label htmlFor="format">Production Format</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="format"
                value={formik.values.format}
                onChange={formik.handleChange}
                name="format"
              >
                <option value="" disabled>
                  Select format...
                </option>
                {formatOptions.map((title) => (
                  <option key={title} value={title}>
                    {title}
                  </option>
                ))}
              </ProductionToolsSelect>
              <FieldError formik={formik} name="format" />
            </div>
            <div className="fg">
              <label htmlFor="genre">Genre</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="genre"
                value={formik.values.genre}
                onChange={formik.handleChange}
                name="genre"
              >
                {genreOptions.length === 0 && (
                  <option value="" disabled>
                    Loading genres...
                  </option>
                )}
                {genreOptions.map((title) => (
                  <option key={title} value={title}>
                    {title}
                  </option>
                ))}
              </ProductionToolsSelect>
            </div>
            <div className="fg">
              <label htmlFor="market">Production Market</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="market"
                value={formik.values.market}
                onChange={formik.handleChange}
                name="market"
              >
                {countryList.length === 0 && (
                  <option value="" disabled>
                    Loading markets...
                  </option>
                )}
                {countryList.map((item) => (
                  <option key={item.id} value={item.title}>
                    {item.title}
                  </option>
                ))}
              </ProductionToolsSelect>
            </div>
          </div>

          <span className="sec-label">Production Scale</span>
          <div className="fgrid3">
            <div className="fg">
              <label htmlFor="totalBudget">Total Budget</label>
              <input
                id="totalBudget"
                type="text"
                placeholder="e.g. 50,00,000 or 500000"
                value={formik.values.totalBudget}
                onChange={formik.handleChange}
                name="totalBudget"
                maxLength={AI_TOOL_LIMITS.BUDGET_STRING_MAX}
              />
              <FieldError formik={formik} name="totalBudget" />
            </div>
            <div className="fg">
              <label htmlFor="shootDays">Shoot Days</label>
              <input
                id="shootDays"
                type="number"
                placeholder="e.g. 20"
                min="1"
                value={formik.values.shootDays}
                onChange={handleDigitsOnlyChange(formik.handleChange)}
                onKeyDown={preventInvalidNumberKeyDown}
                onWheel={preventNumberInputWheelChange}
                name="shootDays"
              />
              <FieldError formik={formik} name="shootDays" />
            </div>
            <div className="fg">
              <label htmlFor="runtime">Runtime / Duration</label>
              <input
                id="runtime"
                type="text"
                placeholder="e.g. 90 mins / 30 sec"
                value={formik.values.runtime}
                onChange={formik.handleChange}
                name="runtime"
                maxLength={AI_TOOL_LIMITS.RUNTIME_MAX}
              />
              <FieldError formik={formik} name="runtime" />
            </div>
          </div>
          <div className="fgrid4">
            <div className="fg">
              <label htmlFor="castCount">Principal Cast</label>
              <input
                id="castCount"
                type="number"
                placeholder="e.g. 5"
                min="0"
                value={formik.values.castCount}
                onChange={handleDigitsOnlyChange(formik.handleChange)}
                onKeyDown={preventInvalidNumberKeyDown}
                onWheel={preventNumberInputWheelChange}
                name="castCount"
              />
              <FieldError formik={formik} name="castCount" />
            </div>
            <div className="fg">
              <label htmlFor="locations">Shoot Locations</label>
              <input
                id="locations"
                type="number"
                placeholder="e.g. 8"
                min="1"
                value={formik.values.locations}
                onChange={handleDigitsOnlyChange(formik.handleChange)}
                onKeyDown={preventInvalidNumberKeyDown}
                onWheel={preventNumberInputWheelChange}
                name="locations"
              />
              <FieldError formik={formik} name="locations" />
            </div>
            <div className="fg">
              <label htmlFor="nightShoots">Night Shoots</label>
              <input
                id="nightShoots"
                type="number"
                placeholder="e.g. 4"
                min="0"
                value={formik.values.nightShoots}
                onChange={handleDigitsOnlyChange(formik.handleChange)}
                onKeyDown={preventInvalidNumberKeyDown}
                onWheel={preventNumberInputWheelChange}
                name="nightShoots"
              />
              <FieldError formik={formik} name="nightShoots" />
            </div>
            <div className="fg">
              <label htmlFor="vfxShots">VFX Shots (est.)</label>
              <input
                id="vfxShots"
                type="number"
                placeholder="e.g. 0"
                min="0"
                value={formik.values.vfxShots}
                onChange={handleDigitsOnlyChange(formik.handleChange)}
                onKeyDown={preventInvalidNumberKeyDown}
                onWheel={preventNumberInputWheelChange}
                name="vfxShots"
              />
              <FieldError formik={formik} name="vfxShots" />
            </div>
          </div>

          <span className="sec-label">Talent &amp; Crew Level</span>
          <div className="fgrid">
            <div className="fg">
              <label htmlFor="castLevel">Cast Level</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="castLevel"
                value={formik.values.castLevel}
                onChange={formik.handleChange}
                name="castLevel"
              >
                <option value="emerging">Emerging / Non-Union</option>
                <option value="indie">Indie / Low-Budget Union</option>
                <option value="established">Established Names</option>
                <option value="stars">Star Driven</option>
              </ProductionToolsSelect>
            </div>
            <div className="fg">
              <label htmlFor="crewLevel">Crew Level</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="crewLevel"
                value={formik.values.crewLevel}
                onChange={formik.handleChange}
                name="crewLevel"
              >
                <option value="indie">Indie / Non-Union</option>
                <option value="mid">Mid-Level</option>
                <option value="union">Full Union (IATSE / DGA)</option>
              </ProductionToolsSelect>
            </div>
            <div className="fg">
              <label htmlFor="locationType">Location Type</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="locationType"
                value={formik.values.locationType}
                onChange={formik.handleChange}
                name="locationType"
              >
                <option value="practical">Practical Locations Only</option>
                <option value="mixed">Practical + Some Studio</option>
                <option value="studio">Primarily Studio / Stage</option>
              </ProductionToolsSelect>
            </div>
            <div className="fg">
              <label htmlFor="postLevel">Post Production Level</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="postLevel"
                value={formik.values.postLevel}
                onChange={formik.handleChange}
                name="postLevel"
              >
                <option value="basic">Basic (edit, color, sound mix)</option>
                <option value="standard">Standard (+ VFX, DCP)</option>
                <option value="premium">Premium (heavy VFX, Dolby)</option>
              </ProductionToolsSelect>
            </div>
          </div>

          <span className="sec-label">Special Elements</span>
          <div className="fgrid">
            <div className="fg">
              <label htmlFor="stunts">Stunts / Action Sequences</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="stunts"
                value={formik.values.stunts}
                onChange={formik.handleChange}
                name="stunts"
              >
                <option value="none">None</option>
                <option value="minor">Minor (1-2 sequences)</option>
                <option value="major">Major Action / Stunts</option>
              </ProductionToolsSelect>
            </div>
            <div className="fg">
              <label htmlFor="period">Period / Costume Heavy</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="period"
                value={formik.values.period}
                onChange={formik.handleChange}
                name="period"
              >
                <option value="contemporary">Contemporary</option>
                <option value="semi">Semi-Period</option>
                <option value="full">Full Period / Costume Heavy</option>
              </ProductionToolsSelect>
            </div>
            <div className="fg">
              <label htmlFor="music">Music (Original Score)</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="music"
                value={formik.values.music}
                onChange={formik.handleChange}
                name="music"
              >
                <option value="minimal">Minimal / Licensed</option>
                <option value="original">Original Score</option>
                <option value="full">Full Score + Songs</option>
              </ProductionToolsSelect>
            </div>
            <div className="fg">
              <label htmlFor="distrib">Distribution Target</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="distrib"
                value={formik.values.distrib}
                onChange={formik.handleChange}
                name="distrib"
              >
                <option value="festival">Film Festival Circuit</option>
                <option value="ott">OTT / Streaming</option>
                <option value="theatrical">Theatrical Release</option>
                <option value="broadcast">Broadcast / TV</option>
              </ProductionToolsSelect>
            </div>
          </div>

          <span className="sec-label">Additional Notes (optional)</span>
          <div className="fg">
            {/* remove resize handle */}
            <textarea
              id="notes"
              rows={3}
              placeholder="Any specific elements — foreign locations, animals, underwater sequences, celebrity cast, international co-production, etc."
              value={formik.values.notes}
              onChange={formik.handleChange}
              name="notes"
              maxLength={AI_TOOL_LIMITS.NOTES_MAX}
              style={{ resize: 'none' }}
            />
            <FieldError formik={formik} name="notes" />
          </div>

          {displayError && <div className="err">{displayError}</div>}

          <AiToolRunButton loading={isSubmitting} onClick={formik.handleSubmit}>
            Generate Budget Breakdown
          </AiToolRunButton>
        </div>

        {analyzing && (
          <div className="stream-wrap">
            <div className="stream-card">
              <div className="stream-label">
                ● AI is building your budget breakdown
              </div>
              <div className="stream-dots">
                <div className="dot" />
                <div className="dot" />
                <div className="dot" />
              </div>
              <div className="stream-msg">{streamMsg}</div>
            </div>
          </div>
        )}
      </div>

      {showResults && budget && inputs && (
        <div ref={resultsRef}>
          <FilmBudgetBreakdownResultsView
            budget={budget}
            inputs={inputs}
            investorStmt={investorStmt}
            intelItems={intelItems}
            aiContent={aiContent}
            aiError={aiError}
            market={inputs.market}
            onReset={resetForm}
          />
        </div>
      )}
    </div>
  )
}

export default FilmBudgetBreakdown
